import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { AdminLayout } from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Users, FileText, DollarSign, UserPlus, RefreshCw, AlertCircle, Activity, Clock } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, hint, color, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-bold mt-1 truncate">{value}</p>
          {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const fmtMoney = (cents) => {
  if (cents == null) return 'R$ 0,00';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export default function AdminOverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, l] = await Promise.all([
        api.admin.getStats(),
        api.admin.getRecentLogins(5),
      ]);
      setStats(s);
      setLogins(l.logins || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
          <span className="ml-2">Carregando…</span>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-500 text-lg">{error}</p>
          <Button onClick={load} className="mt-4">Tentar novamente</Button>
        </div>
      </AdminLayout>
    );
  }

  const totalUsers = stats?.users?.total || 0;
  const activeUsers = stats?.users?.active_this_week || 0;
  const totalConsultations = stats?.consultations?.total || 0;
  const consultationsThisMonth = stats?.consultations?.this_month || 0;
  const mrrCents = stats?.revenue?.mrr_cents ?? stats?.revenue?.estimated_monthly_cents ?? 0;
  const newSignups7 = stats?.users?.new_signups_7d || 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-overview-title">Visão Geral</h1>
            <p className="text-gray-500">Métricas em tempo real da plataforma Akali AI</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="admin-overview-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
        </div>

        {/* Top metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Usuários ativos"
            value={activeUsers}
            hint={`${totalUsers} cadastrados no total`}
            color="bg-amber-500"
            testId="metric-active-users"
          />
          <StatCard
            icon={FileText}
            label="Consultas geradas"
            value={totalConsultations}
            hint={`${consultationsThisMonth} este mês`}
            color="bg-blue-500"
            testId="metric-consultations"
          />
          <StatCard
            icon={DollarSign}
            label="MRR atual"
            value={fmtMoney(mrrCents)}
            hint="Stripe live"
            color="bg-emerald-500"
            testId="metric-mrr"
          />
          <StatCard
            icon={UserPlus}
            label="Novos cadastros"
            value={newSignups7}
            hint="Últimos 7 dias"
            color="bg-purple-500"
            testId="metric-signups-7d"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent logins */}
          <Card className="lg:col-span-2" data-testid="recent-logins-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" /> Últimos 5 acessos
              </CardTitle>
              <CardDescription>Quem entrou no app mais recentemente</CardDescription>
            </CardHeader>
            <CardContent>
              {logins.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">Nenhum login registrado ainda.</p>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                  {logins.map((l, i) => (
                    <li key={l.user_id || i} className="py-3 flex items-center justify-between gap-3" data-testid={`recent-login-${i}`}>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{l.full_name || l.email}</p>
                        <p className="text-xs text-gray-500 truncate">{l.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {l.last_login_at ? new Date(l.last_login_at).toLocaleString('pt-BR') : '—'}
                        </p>
                        <Badge variant="outline" className="text-[10px] mt-1">{l.plan_type || 'starter'}</Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Plans summary */}
          <Card data-testid="plans-summary-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-500" /> Distribuição
              </CardTitle>
              <CardDescription>Por plano</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(stats?.plans || {}).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between">
                  <span className="text-sm capitalize">{plan}</span>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              ))}
              <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/admin/users')} data-testid="overview-go-users">
                Ver todos os usuários →
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
